import React, { useState, useEffect } from "react";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { addPublicNotice, addPrivateNotice } from "../../redux/noticeSlice";
import { fetchResidences } from "../../redux/userSlice";
import { notification } from "antd";

const Notices = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { residences, loading } = useSelector((state) => state.user);
  const [noticeType, setNoticeType] = useState("public"); // "public" | "private"
  const [formData, setFormData] = useState({
    title: "",
    notice_body: "",
    houses: [], 
  });
  const [errors, setErrors] = useState({});
  const [isSending, setIsSending] = useState(false);

  
  useEffect(() => {
    if (location.state) {
      const { noticeType, selectedUser } = location.state;

      setNoticeType(noticeType);

      setFormData((prev) => ({
        ...prev,
        houses: Array.isArray(selectedUser)
          ? selectedUser.map((u) => String(u.id))
          : selectedUser
          ? [String(selectedUser.id)]
          : [],
      }));
    }
  }, [location.state]);

 
  useEffect(() => {
    if (noticeType === "private") {
      dispatch(fetchResidences());
    }
  }, [dispatch, noticeType]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleResidenceChange = (selectedOptions) => {
    const selectedIds = selectedOptions ? selectedOptions.map((o) => o.value) : [];

    setFormData((prev) => ({ ...prev, houses: selectedIds }));
    setErrors((prev) => ({ ...prev, houses: null }));
  };

  
  const validateForm = () => {
    const newErrors = {};

    if (!formData.title) newErrors.title = "Title is required.";
    if (!formData.notice_body) newErrors.notice_body = "Notice description is required.";
    if (noticeType === "private" && formData.houses.length === 0) {
      newErrors.houses = "At least one residence must be selected.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

 
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) return;

    setIsSending(true);

   
    const basePayload = {
      title: formData.title,
      notice_body: formData.notice_body,
      notice_type: noticeType, // "public" or "private"
    };

    const payload =
      noticeType === "public"
        ? basePayload                              
        : { ...basePayload, houses: formData.houses }; 

    console.log("Payload →", payload);

  
    const thunk = noticeType === "public" ? addPublicNotice : addPrivateNotice;

    dispatch(thunk(payload))
      .unwrap()
      .then(() => {
        notification.success({
          message: `${noticeType.charAt(0).toUpperCase() + noticeType.slice(1)} Notice Sent`,
          description: `${noticeType.charAt(0).toUpperCase() + noticeType.slice(1)} notice sent successfully.`,
        });
        navigate("/dashboard/noticelist");
      })
      .catch((error) => {
        console.error("Error:", error);
        notification.error({
          message: error.error ? "Validation Error" : "Error",
          description: error.error || "Network request failed. Please try again.",
        });
      })
      .finally(() => setIsSending(false));
  };

  
  return (
    <div className="bg-white pb-20 mx-6">
      <h1 className="text-xl font-semibold mb-4">Add Notices</h1>

      <div className="bg-gray-300 rounded-lg p-4">
      
        <div className="flex space-x-4 ml-3">
          <button
            type="button"
            className={`px-4 py-2 ${
              noticeType === "public"
                ? "bg-[#403F93] text-white rounded-lg"
                : "bg-gray-100 rounded-lg"
            }`}
            onClick={() => setNoticeType("public")}
          >
            Public Notice
          </button>
          <button
            type="button"
            className={`px-4 py-2 ${
              noticeType === "private"
                ? "bg-[#403F93] text-white rounded-lg"
                : "bg-gray-100 rounded-lg"
            }`}
            onClick={() => setNoticeType("private")}
          >
            Private Notice
          </button>
        </div>

        
        <div className="py-6 bg-white rounded-lg m-3 p-4">
          <h3 className="font-bold text-[22px] mb-4">
            {noticeType === "public" ? "Send Public Notice" : "Send Private Notice"}
          </h3>

          <form onSubmit={handleSubmit}>
            {/* Title */}
            <div>
              <label className="font-bold text-md">Notice Title</label>
              <input
                className={`rounded-md py-3 px-4 w-full border-[2px] border-gray-400 mt-2 ${
                  errors.title ? "border-red-500" : ""
                }`}
                type="text"
                name="title"
                placeholder="Notice Title"
                value={formData.title}
                onChange={handleInputChange}
                disabled={isSending}
              />
              {errors.title && <p className="text-red-500 text-sm">{errors.title}</p>}
            </div>

            {/* Description */}
            <div className="pt-6">
              <label className="font-bold text-md">Notice Description</label>
              <textarea
                className={`rounded-md w-full py-4 px-4 border-[2px] border-gray-400 mt-2 ${
                  errors.notice_body ? "border-red-500" : ""
                }`}
                name="notice_body"
                placeholder="Notice Description"
                value={formData.notice_body}
                onChange={handleInputChange}
                disabled={isSending}
              />
              {errors.notice_body && (
                <p className="text-red-500 text-sm">{errors.notice_body}</p>
              )}
            </div>

            {/* Private-only: Residence selector */}
            {noticeType === "private" && (
              <div className="pt-6">
                <label className="font-bold text-md">Select Residences</label>
                <Select
                  isMulti
                  isDisabled={loading || isSending}
                  options={
                    residences?.data?.map((residence) => ({
                      value: residence.id,
                      label: residence.house_number,
                    })) || []
                  }
                  onChange={handleResidenceChange}
                  className="mt-2"
                />

                {errors.users && (
                  <p className="text-red-500 text-sm">{errors.users}</p>
                )}
              </div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              className="bg-green-800 text-white flex px-8 py-2 rounded-lg mt-6"
              disabled={isSending}
            >
              {isSending ? "Sending..." : "Send Notice"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Notices;